import {
  CatalogRecHeader,
  CatalogRecHeaderAdditional,
  CatalogRecHeaderImage,
  CatalogRecHeaderLabel,
  CatalogRecHeaderLicense,
  CatalogRecHeaderSublabel,
  CatalogRecHeaderTag,
  CatalogXmlFetch,
  CatalogXmlFetchDetail
} from '@aipb/gulliver/catalog/model';
import { parseXML } from '@aipb/shared/util/other';
import { Injectable } from '@angular/core';
import { Element } from 'xml-js';

export const HIGHLIGHT_REGEX = /(\|\|)(.+?)(\/\|\|)/g;

@Injectable({
  providedIn: 'root'
})
export class CatalogXmlParserService {
  parseFetchDetail(xml: string): CatalogXmlFetchDetail {
    const parsed = parseXML(xml);

    const label = parsed.elements[0].attributes['label'];
    return {
      title: label,
      report: xml
    };
  }

  parseCastorHeader(id: string, xml: string): CatalogRecHeader {
    let result: CatalogRecHeader;

    try {
      const parsed = parseXML(xml);
      const rootElements = parsed.elements[0].elements;
      result = {
        id: this.parseId(rootElements),
        license: this.parseLicense(rootElements),
        label: this.parseLabel(rootElements),
        sublabel: this.parseSublabel(rootElements),
        additional: this.parseAdditional(rootElements),
        images: this.parseImages(rootElements),
        tags: this.parseTags(rootElements),
        icons: this.parseIcons(rootElements)
      };
    } catch (e) {
      result = {
        id: id,
        label: {
          value: 'Chyba v datech'
        },
        sublabel: {
          value: ''
        },
        additional: {
          value: ''
        },
        icons: [],
        images: [],
        license: {
          type: '',
          value: '',
          logo: null
        }
      };
    }

    return result;
  }

  parseCastorFetch3if(xmls: string[] | string): CatalogXmlFetch[] {
    if (!xmls) return [];

    const fetches = [];

    if (typeof xmls === 'string') {
      xmls = [xmls];
    }

    for (const xml of xmls) {
      const parsed = parseXML(xml);

      const rootElements = parsed.elements[0].elements;

      const url = this.parseFetchUrl(rootElements);

      if (!url) continue;

      fetches.push({
        label: this.parseTranslation(rootElements.find((f) => f.name === 'label')),
        url: url,
        icons: this.parseIcons(rootElements)
      });
    }

    return fetches;
  }

  private parseFetchUrl(root: Element[]): string {
    const itemUrlElements = root.filter((f) => {
      return f.name === 'item';
    });

    if (itemUrlElements.length > 0) {
      return itemUrlElements[0] && itemUrlElements[0].elements[0] && 'text' in itemUrlElements[0].elements[0]
        ? itemUrlElements[0].elements[0].text + ''
        : null;
    }

    return null;
  }

  private parseId(root: Element[]): string {
    return root.find((r) => r.name === 'id').elements[0].text + '';
  }

  private parseLicense(root: Element[]): CatalogRecHeaderLicense {
    const licenseElement = root.find((r) => r.name === 'licence');

    const type = licenseElement ? licenseElement.elements.find((e) => e.name === 'type')?.elements[0].text : '';
    const logo = licenseElement ? licenseElement.elements.find((e) => e.name === 'logo')?.elements[0].text : '';
    const csUrl = licenseElement ? licenseElement.elements.find((e) => e.name === 'url')?.elements[0].text : '';
    const enUrl = licenseElement ? licenseElement.elements.find((e) => e.name === 'url_en')?.elements[0].text : '';
    const deUrl = licenseElement ? licenseElement.elements.find((e) => e.name === 'url_de')?.elements[0].text : '';

    return {
      type: type + '',
      logo: logo + '',
      url: {
        cs: csUrl + '',
        en: enUrl + '',
        de: deUrl + ''
      },
      value:
        licenseElement && licenseElement.elements.length > 0 && licenseElement.elements[0].text
          ? licenseElement.elements[0].text + ''
          : ''
    };
  }

  private parseLabel(root: Element[]): CatalogRecHeaderLabel {
    if (root.find((r) => r.name === 'label').elements.length === 0) {
      return {
        value: ''
      };
    }

    return {
      value: this.highlight(root.find((r) => r.name === 'label').elements[0].text + '')
    };
  }

  private parseSublabel(root: Element[]): CatalogRecHeaderSublabel {
    const sublabel = root.find((r) => r.name === 'sublabel');

    if (!sublabel) {
      return {
        value: ''
      };
    }

    if (sublabel && sublabel.elements.length === 0) {
      return {
        value: ''
      };
    }

    return {
      value: this.highlight(root.find((r) => r.name === 'sublabel').elements[0].text + '')
    };
  }

  private parseAdditional(root: Element[]): CatalogRecHeaderAdditional {
    if (!root.find((r) => r.name === 'additional')) {
      return {
        value: ''
      };
    }

    if (root.find((r) => r.name === 'additional').elements.length === 0) {
      return {
        value: ''
      };
    }

    return {
      value: this.highlight(root.find((r) => r.name === 'additional').elements[0].text + '')
    };
  }

  private parseImages(root: Element[]): CatalogRecHeaderImage[] {
    const images = root.find((r) => r.name === 'images');

    if (!images) {
      return [];
    }

    const imageElements = images.elements[0].elements;

    const alt = imageElements.find((f) => f.name === 'alt');
    const altText = alt && alt.elements[0] && alt.elements[0].text ? alt.elements[0].text + '' : '';

    return imageElements
      .filter((f) => f.elements.length > 0 && (f.name === 'main' || f.name === 'thumb'))
      .map((data) => ({
        alt: altText,
        thumb: this.removeHighlight(data.elements[0].text + '') || ''
      }));
  }

  private parseTags(root: Element[]): CatalogRecHeaderTag[] {
    const tags = root.find((r) => r.name === 'tags');

    if (!tags) return [];

    return tags.elements
      .filter((data) => data.elements.filter((f) => f.type === 'text').length > 0)
      .map((data) => {
        const prefix =
          tags.elements[0].attributes && tags.elements[0].attributes['prefix']
            ? tags.elements[0].attributes['prefix'] + ''
            : '';

        return {
          prefix: prefix,
          value: this.highlight(data.elements.find((f) => f.type === 'text').text + ''),
          context: data.elements.some((f) => f.name === 'context')
            ? data.elements.find((f) => f.name === 'context').elements.find((f) => f.type === 'text').text + ''
            : null
        };
      });
  }

  private parseIcons(root: Element[]): { value: string }[] {
    const icons = root.find((r) => r.name === 'icons');

    if (!icons) return [];

    return icons.elements
      .filter((data) => data.elements.filter((f) => f.type === 'text').length > 0)
      .map((data) => ({
        value: data.elements[0].text + ''
      }));
  }

  private parseTranslation(element: Element): { [key: string]: string } {
    const labels = {};

    element.elements.forEach((el) => {
      labels[el.name] = el.elements.length > 0 ? el.elements[0].text : '<undefined-translation>';
    });

    return labels;
  }

  private highlight(s: string): string {
    return s.replace(HIGHLIGHT_REGEX, '<em>$2</em>');
  }

  private removeHighlight(s: string): string {
    return s.replace(HIGHLIGHT_REGEX, '$2');
  }
}
